import { portalConfig } from './portal-config.js';

export class HttpError extends Error {
  constructor(public readonly status: number, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.name = 'HttpError';
  }
}

/** The id token no longer authorizes; the caller should refresh the session, not sign out. */
export class UnauthorizedError extends Error {
  constructor() {
    super('unauthorized');
    this.name = 'UnauthorizedError';
  }
}

// A token about to expire is treated the same as one that just has - the authorizer rejects both.
const CLOCK_SKEW_MS = 30_000;

function decodeJwtExpMs(token: string): number | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    let b64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const { exp } = JSON.parse(atob(b64)) as { exp?: unknown };
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string, nowMs = Date.now()): boolean {
  const expMs = decodeJwtExpMs(token);
  return expMs !== null && nowMs >= expMs - CLOCK_SKEW_MS;
}

// A missing Authorization header is a 401 from API Gateway itself, but the common case - a present
// but expired id token - is rejected by the SIMPLE Lambda authorizer, which surfaces as 403. That 403
// is an expiry signal too, so both reach the same refresh path. A 403 with a still-valid token is a
// genuine denial (wrong hosted domain) and must surface as one - refreshing it would loop.
function throwIfUnauthorized(res: Response, idToken: string): void {
  if (res.status === 401 || (res.status === 403 && isTokenExpired(idToken))) {
    throw new UnauthorizedError();
  }
}

export async function apiGet<T>(path: string, idToken: string): Promise<T> {
  const res = await fetch(`${portalConfig.apiBase}${path}`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  throwIfUnauthorized(res, idToken);
  if (!res.ok) {
    throw new HttpError(res.status);
  }
  return res.json() as Promise<T>;
}

/**
 * A POST whose non-2xx answers can be expected outcomes rather than failures: POST /sweep answers 429
 * during its cooldown, and that body is what tells the viewer when the last sweep started. Statuses
 * listed in `expected` come back with their body; anything else unsuccessful still throws.
 */
export async function apiPost<T>(path: string, idToken: string, expected: number[] = []): Promise<{ status: number; body: T }> {
  const res = await fetch(`${portalConfig.apiBase}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  throwIfUnauthorized(res, idToken);
  if (!res.ok && !expected.includes(res.status)) {
    throw new HttpError(res.status);
  }
  return { status: res.status, body: (await res.json()) as T };
}
